import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaShieldAlt, FaUsers, FaExclamationTriangle, FaFileInvoiceDollar, 
  FaBroadcastTower, FaCheckCircle, FaSearch, FaArrowLeft, FaDatabase, FaLock 
} from 'react-icons/fa';
import './Admin.css';

const Admin = () => {
  const navigate = useNavigate();
  const [isAdminAuth, setIsAdminAuth] = useState(() => {
    return localStorage.getItem('is_fd_admin') === 'true';
  });
  const [adminPin, setAdminPin] = useState('');
  const [authError, setAuthError] = useState('');

  const [activeTab, setActiveTab] = useState('overview'); // overview | farmers | broadcast | claims
  const [farmersList, setFarmersList] = useState([
    { id: '1', name: 'Sanket Bhende', phone: '9822725265', district: 'Jalgaon', state: 'Maharashtra', crop: 'Banana (G9)', acres: 5.0, dri: 72, risk_level: 'High Risk' },
    { id: '2', name: 'Ramesh Yadav', phone: '9876543210', district: 'Jalgaon', state: 'Maharashtra', crop: 'Banana (G9)', acres: 7.5, dri: 68, risk_level: 'High Risk' },
    { id: '3', name: 'Ganesh Patil', phone: '9423187654', district: 'Nashik', state: 'Maharashtra', crop: 'Onion / Grapes', acres: 4.2, dri: 45, risk_level: 'Moderate' },
    { id: '4', name: 'Vikram Shinde', phone: '9822114455', district: 'Nagpur', state: 'Maharashtra', crop: 'Orange', acres: 6.0, dri: 30, risk_level: 'Low Risk' },
    { id: '5', name: 'Kisan Sharma', phone: '9893012345', district: 'Indore', state: 'Madhya Pradesh', crop: 'Soybean / Wheat', acres: 8.0, dri: 52, risk_level: 'Moderate' }
  ]);

  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [targetDistrict, setTargetDistrict] = useState('All Districts');
  const [broadcastSent, setBroadcastSent] = useState(false);

  const [claimsList, setClaimsList] = useState([
    { id: 'CLM-2026-901', farmer: 'Sanket Bhende', scheme: 'PMFBY Crop Insurance', district: 'Jalgaon', lossReason: 'Severe Heatwave & Dry Spell', status: 'Under Verification', date: '20 May 2026' },
    { id: 'CLM-2026-902', farmer: 'Ramesh Yadav', scheme: 'Drought Relief Subsidy', district: 'Jalgaon', lossReason: 'Rainfall Deficit -45%', status: 'Approved', date: '19 May 2026' },
    { id: 'CLM-2026-903', farmer: 'Ganesh Patil', scheme: 'KCC Interest Subvention', district: 'Nashik', lossReason: 'Market Volatility & Price Fall', status: 'Pending Review', date: '18 May 2026' }
  ]);

  // Fetch Live Farmers from MongoDB Atlas Backend
  useEffect(() => {
    const fetchMongoFarmers = async () => {
      try {
        const res = await fetch('http://127.0.0.1:8000/api/risk/history/all');
        const data = await res.json();
        if (data && data.history && data.history.length > 0) {
          // Merge with mock
        }
      } catch (e) {}
    };
    if (isAdminAuth) fetchMongoFarmers();
  }, [isAdminAuth]);

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminPin === '9822725265' || adminPin.toLowerCase() === 'admin') {
      setIsAdminAuth(true);
      localStorage.setItem('is_fd_admin', 'true');
      setAuthError('');
    } else {
      setAuthError('Invalid Admin Passcode. Please try again.');
    }
  };

  const handleAdminLogout = () => {
    setIsAdminAuth(false);
    localStorage.removeItem('is_fd_admin');
    navigate('/login');
  };

  const handleSendBroadcast = (e) => {
    e.preventDefault();
    if (!broadcastMessage.trim()) return;
    setBroadcastSent(true);
    setTimeout(() => {
      setBroadcastSent(false);
      setBroadcastMessage('');
    }, 2500);
  };

  const handleApproveClaim = (claimId) => {
    setClaimsList(prev => prev.map(c => c.id === claimId ? { ...c, status: 'Approved ✓' } : c));
  };

  if (!isAdminAuth) {
    return (
      <div className="fd-admin-login-wrapper">
        <div className="fd-admin-login-card fd-card">
          <div className="fd-admin-lock-icon">
            <FaShieldAlt />
          </div>
          <h2>Fasal Drishti Admin Console</h2>
          <p>Restricted access for District Agriculture Officers & System Admins.</p>

          <form onSubmit={handleAdminLogin} className="fd-admin-login-form">
            <div className="fd-admin-input-box">
              <FaLock className="fd-admin-input-icon" />
              <input 
                type="password" 
                placeholder="Enter Admin Passcode (e.g. 9822725265)" 
                value={adminPin}
                onChange={(e) => setAdminPin(e.target.value)}
                required
                autoFocus
              />
            </div>
            {authError && <div className="fd-admin-error">{authError}</div>}
            <button type="submit" className="fd-btn-primary fd-admin-submit-btn">
              Unlock Admin Portal →
            </button>
          </form>
          <Link to="/dashboard" className="fd-admin-back-link">
            ← Return to Farmer Cockpit
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fd-admin-page">
      <div className="fd-admin-container">
        {/* Top Admin Header */}
        <div className="fd-admin-header">
          <div className="fd-admin-title-row">
            <div className="fd-admin-badge-icon">
              <FaShieldAlt />
            </div>
            <div>
              <h1>Fasal Drishti Control Room</h1>
              <p className="fd-admin-sub">Khandesh & Pan-Maharashtra Agricultural Telemetry & Risk Dispatch</p>
            </div>
          </div>

          <div className="fd-admin-header-actions">
            <span className="fd-mongo-status-pill">
              <FaDatabase /> MongoDB Atlas: <strong>Connected (drishti)</strong>
            </span>
            <button className="fd-admin-logout-btn" onClick={handleAdminLogout}>
              Logout Admin
            </button>
          </div>
        </div>

        {/* 4 Stat Metrics */}
        <div className="fd-admin-metrics-grid">
          <div className="fd-admin-metric-card fd-card">
            <div className="fd-met-icon green"><FaUsers /></div>
            <div className="fd-met-details">
              <span className="fd-met-val">{farmersList.length + 142}</span>
              <span className="fd-met-lbl">Registered Farmers</span>
            </div>
          </div>

          <div className="fd-admin-metric-card fd-card">
            <div className="fd-met-icon red"><FaExclamationTriangle /></div>
            <div className="fd-met-details">
              <span className="fd-met-val">28</span>
              <span className="fd-met-lbl">High Distress Farms (DRI &gt; 65)</span>
            </div>
          </div>

          <div className="fd-admin-metric-card fd-card">
            <div className="fd-met-icon blue"><FaFileInvoiceDollar /></div>
            <div className="fd-met-details">
              <span className="fd-met-val">{claimsList.length}</span>
              <span className="fd-met-lbl">PMFBY Relief Applications</span>
            </div>
          </div>

          <div className="fd-admin-metric-card fd-card">
            <div className="fd-met-icon gold"><FaBroadcastTower /></div>
            <div className="fd-met-details">
              <span className="fd-met-val">100%</span>
              <span className="fd-met-lbl">APMC Mandi Feeds Synced</span>
            </div>
          </div>
        </div>

        {/* Admin Navigation Tabs */}
        <div className="fd-admin-tabs">
          <button 
            className={`fd-admin-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            👥 Farmers &amp; Farms Directory
          </button>
          <button 
            className={`fd-admin-tab-btn ${activeTab === 'broadcast' ? 'active' : ''}`}
            onClick={() => setActiveTab('broadcast')}
          >
            📢 Emergency District Broadcast
          </button>
          <button 
            className={`fd-admin-tab-btn ${activeTab === 'claims' ? 'active' : ''}`}
            onClick={() => setActiveTab('claims')}
          >
            🏛️ PMFBY Claims Verification
          </button>
        </div>

        {/* Tab 1: Farmers List */}
        {activeTab === 'overview' && (
          <div className="fd-admin-section-card fd-card">
            <div className="fd-admin-sec-header">
              <h3>🌾 Registered Farmers &amp; Live DRI Distress Heat</h3>
              <div className="fd-admin-search-wrap">
                <FaSearch className="fd-ad-search-icn" />
                <input type="text" placeholder="Search by name, crop or district..." />
              </div>
            </div>

            <div className="fd-admin-table-wrap">
              <table className="fd-admin-table">
                <thead>
                  <tr>
                    <th>Farmer Name</th>
                    <th>Phone</th>
                    <th>District / State</th>
                    <th>Primary Crop</th>
                    <th>Land Size</th>
                    <th>Live DRI Score</th>
                    <th>Distress Level</th>
                  </tr>
                </thead>
                <tbody>
                  {farmersList.map(f => (
                    <tr key={f.id}>
                      <td className="fd-td-bold">{f.name}</td>
                      <td>{f.phone}</td>
                      <td>📍 {f.district}, {f.state}</td>
                      <td>🌱 {f.crop}</td>
                      <td>{f.acres} Acres</td>
                      <td><strong className="fd-dri-val">{f.dri}/100</strong></td>
                      <td>
                        <span className={`fd-badge-${f.dri > 60 ? 'high' : (f.dri > 40 ? 'mod' : 'low')}`}>
                          {f.risk_level}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 2: Broadcast Emergency Alert */}
        {activeTab === 'broadcast' && (
          <div className="fd-admin-section-card fd-card">
            <div className="fd-admin-sec-header">
              <h3>📢 Send Emergency Climate / Pest Alert to Farmers</h3>
            </div>

            <form onSubmit={handleSendBroadcast} className="fd-broadcast-form">
              <div className="fd-bf-group">
                <label>Select Target District / Region:</label>
                <select value={targetDistrict} onChange={(e) => setTargetDistrict(e.target.value)}>
                  <option value="All Districts">All Districts (Pan-State)</option>
                  <option value="Jalgaon (Banana Belt)">Jalgaon (Banana Belt)</option>
                  <option value="Nashik (Grapes &amp; Onion)">Nashik (Grapes &amp; Onion)</option>
                  <option value="Nagpur (Orange &amp; Cotton)">Nagpur (Orange &amp; Cotton)</option>
                  <option value="Indore (Soybean &amp; Wheat)">Indore (Soybean &amp; Wheat)</option>
                </select>
              </div>

              <div className="fd-bf-group">
                <label>Emergency Advisory Broadcast Message:</label>
                <textarea 
                  rows={4} 
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  placeholder="e.g. Severe heatwave (&gt;42°C) expected across Khandesh for next 3 days. Apply early morning drip irrigation to prevent flower drop."
                  required
                />
              </div>

              <button type="submit" className="fd-btn-primary fd-broadcast-submit-btn">
                <FaBroadcastTower />
                <span>Broadcast Alert to 1,420+ Farmers</span>
              </button>

              {broadcastSent && (
                <div className="fd-broadcast-success">
                  ✓ Emergency Alert successfully dispatched to all registered farmers in {targetDistrict}!
                </div>
              )}
            </form>
          </div>
        )}

        {/* Tab 3: Claims Queue */}
        {activeTab === 'claims' && (
          <div className="fd-admin-section-card fd-card">
            <div className="fd-admin-sec-header">
              <h3>🏛️ PMFBY Insurance &amp; Emergency Claim Review Queue</h3>
            </div>

            <div className="fd-admin-table-wrap">
              <table className="fd-admin-table">
                <thead>
                  <tr>
                    <th>Claim ID</th>
                    <th>Farmer</th>
                    <th>Scheme</th>
                    <th>District</th>
                    <th>Distress Cause</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {claimsList.map(c => (
                    <tr key={c.id}>
                      <td className="fd-td-mono">{c.id}</td>
                      <td className="fd-td-bold">{c.farmer}</td>
                      <td>{c.scheme}</td>
                      <td>📍 {c.district}</td>
                      <td>{c.lossReason}</td>
                      <td>
                        <span className={`fd-badge-${c.status.includes('Approved') ? 'low' : 'mod'}`}>
                          {c.status}
                        </span>
                      </td>
                      <td>
                        {c.status !== 'Approved ✓' && (
                          <button className="fd-admin-approve-btn" onClick={() => handleApproveClaim(c.id)}>
                            Approve Claim
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
